import { type ImageAdapter, VirtualViewportProxy } from "../image-adapter";
import type { OutputViewportResolver } from "../output-viewport-strategy";
import type { ViewportLiteral } from "../pixel-accessor";
import type { ReversePixelMapper } from "../pixel-mapper";
import type { ColorResampler } from "../color-resampler";
import type {
  DistortionProcessorFactory,
  DistortionProcessorFactoryOptions,
} from "./DistortionProcessorFactory";

/**
 * DistortionController options.
 */
export interface DistortionControllerOptions
  extends DistortionProcessorFactoryOptions {
  /**
   * Destination image viewport option.
   * * When set to ViewportLiteral (or Viewport, which also implements ViewportLiteral) object, output image will
   *  have such viewport.
   * * When set to 'bestFit' string or boolean true, best-fit viewport will be used if possible.
   * * When set to false or omitted, source image viewport will be used.
   */
  viewport?: "bestFit" | boolean | ViewportLiteral;
}

/**
 * Distortion process controller.
 */
export class DistortionController {
  /**
   * DistortionController constructor.
   *
   * @param outputViewportResolver
   * @param distortionProcessorFactory
   */
  constructor(
    private outputViewportResolver: OutputViewportResolver,
    private distortionProcessorFactory: DistortionProcessorFactory
  ) {}

  /**
   * Performs distortion of given image and returns distorted image.
   *
   * @param image
   * @param reversePixelMapper
   * @param resampler
   * @param options
   */
  async distort<ResourceType>(
    image: ImageAdapter<ResourceType>,
    reversePixelMapper: ReversePixelMapper,
    resampler: ColorResampler,
    options: DistortionControllerOptions = {}
  ): Promise<ImageAdapter<ResourceType>> {
    const {
      viewport = false,
      outputScaling = 1,
      repage,
      asyncTimeout = 50,
      abortSignal,
    } = options;

    const targetImage = this.createTargetImage(
      image,
      reversePixelMapper,
      viewport,
      outputScaling
    );

    // Wrap target image adapter into virtual viewport proxy to support virtual viewport offset.
    const virtualViewportProxy = new VirtualViewportProxy(targetImage);

    const distortionProcessor = this.distortionProcessorFactory.create({
      outputScaling,
      repage,
      asyncTimeout,
      abortSignal,
    });

    const result = await distortionProcessor.process(
      virtualViewportProxy,
      resampler
    );

    if (result instanceof VirtualViewportProxy) {
      return result.getAdapter();
    }

    return result;
  }

  /**
   * Creates blank target image which will be filled by pixel colors in distortion processor.
   *
   * @param image
   * @param reversePixelMapper
   * @param viewport
   * @param outputScaling
   * @private
   */
  private createTargetImage<ResourceType>(
    image: ImageAdapter<ResourceType>,
    reversePixelMapper: ReversePixelMapper,
    viewport: "bestFit" | boolean | ViewportLiteral,
    outputScaling: number
  ): ImageAdapter<ResourceType> {
    const outputViewport = this.outputViewportResolver.resolve(
      image,
      reversePixelMapper,
      viewport
    );
    if (outputScaling !== 1) {
      outputViewport.scale(outputScaling);
    }
    return image.getBlank(outputViewport);
  }
}
